import { useEffect, useCallback, useState, useMemo, useRef } from 'react'

import _ from 'lodash'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'


import { useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import { useSelector } from 'react-redux'
import { RootState } from 'slices'
import { useAppDispatch } from 'store'

import client from 'api'



import { BaseLookup } from '../base-lookup'
import { useIxLookupContext } from 'contexts/lookup-context'
import { ixStringToInt } from 'helpers/string-utils'
import { useLookupFetch, LookupFetchConfig, useFiltersList } from 'hooks'
import { fetchLookupData, lookupDataSelector } from 'slices/lookup-data-slice'
import {
    useContextSelfCallback,
    useSelectionChangedCallback,
    useInputChangedCallback,
} from 'hooks'


const _valueExpr = 'HDR_H1_USERID'

const inputRe = [
/^(?<PRINTERNAME>\w+)$/,
]

const inputFormat = (x: any) => {
    return x ? `${x.PRINTERNAME}` : ''
}

const shouldUpdateConfig: any[] = []

const fetchConfig: LookupFetchConfig = {
    url: '/api/ixweb/ixprnsrvcprinter/',
    fields: 'HDR_ID,HDR_H1_USERID,HDR_H1_TIMESTAMP,HDR_H2_USERID,HDR_H2_TIMESTAMP,HDR_VERSION,PRINTERNAME,PRINTERIDENTIFIER',
    allowedFilters: ['hdr_h1_userid', 'printername'],
}

const castToType = (val: any) => {
    return Number(val)
}

export const IxPrnSrvcPrinterLookupFieldsConf = {
    'HDR_ID': { type: 'number' },
    'HDR_H1_USERID': { type: 'number' },
    'HDR_H1_TIMESTAMP': { type: 'date' },
    'HDR_H2_USERID': { type: 'number' },
    'HDR_H2_TIMESTAMP': { type: 'date' },
    'HDR_VERSION': { type: 'number' },
    'PRINTERNAME': { type: 'string' },
    'PRINTERIDENTIFIER': { type: 'string' },
}

export const useIxPrnSrvcPrinterLookupData = (
    active=true,
    valueExpr: string = _valueExpr,
    _?: any,
) => {
    const dispatch = useAppDispatch()
    const params = useMemo<any>(() => {
        let params = {}
        return params
    }, [])
    const { data } = useSelector((state: RootState) => lookupDataSelector(
        state,
        'IxPrnSrvcPrinterLookup',
        params
    ))
    const [lookupMap, setLookupMap] = useState<any>(null)

    const {value: number} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupNumberFormat))

    const loadData = useCallback((force=false) => {
        dispatch(fetchLookupData({ lookupName: 'IxPrnSrvcPrinterLookup', url: fetchConfig.url, params, force }))
    }, [dispatch, params])

    useEffect(() => {
        loadData()
    }, [loadData])

    const format = useCallback((x: any) => {
        return `${number.prefix}${x.PRINTERNAME !== null && x.PRINTERNAME !== undefined ? x.PRINTERNAME : ''}${number.suffix}`
    }, [number])

    useEffect(() => {
        if (data) {
            const typesMap = data.map((t: any) => ({
                id: t[valueExpr],
                value: format(t),
            }))
            setLookupMap(typesMap)
        }
    }, [data, format, valueExpr])

    const getFormat = useCallback((hdr_h1_userid: any, ) => {
        if (lookupMap) {
            const item = lookupMap.find((x: any) => {
                return x.id == hdr_h1_userid  // eslint-disable-line eqeqeq
            })
            return item ? item.value : ''
        }
        return ''
    }, [lookupMap])

    const getLookupValue = useCallback((val: any) => {
        
        if (data) {
            const item = data.find((x: any) => {
                return x[valueExpr] == val  // eslint-disable-line eqeqeq
            })

            return item
        }
        return undefined
    }, [data, valueExpr])

    return {
        data,
        format,
        getFormat,
        getLookupValue,
        loadData,
        lookupMap,
    }
}


export type IIxPrnSrvcPrinterLookupProps = {
    name: string,  // field name in the formik form, would also be used in the lookup context
    readOnly: boolean,
    active?: boolean,
    label?: string,
    onLoadingChanged?: (name: string, isLoading: boolean) => void,
    selectionMode?: 'single' | 'multi' | 'multicsv' | 'range' | 'singlerange',
    getFirstCtxValue?: boolean,
    visible?: boolean,
    openDropdown?: boolean,
    onInfoChange?: any,
    valueExpr?: string,
    updateField?: any,
}

export const IxPrnSrvcPrinterLookup = ({
    name,
    readOnly,
    active=true,
    label,
    onLoadingChanged,
    selectionMode='single',
    getFirstCtxValue=false,
    visible=true,
    openDropdown=false,
    onInfoChange,
    valueExpr=_valueExpr,
    updateField,
}: IIxPrnSrvcPrinterLookupProps) => {
    const { t } = useTranslation()

    const baseLookup = useRef<any>(null)
    const [field] = useField(name)

    const [firstRender, setFirstRender] = useState(true)
    const lastFormikValue = useRef(null)

    const {value: number} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupNumberFormat))

    const [value, setValue] = useState<any>(null)
    const [callbacks, setCallbacks] = useState<{[k: string]: (v: any, force?: boolean) => void}>({})
    const { data, load, loadItem, loading, reset, setData, dataLoaded, lastParams } = useLookupFetch(fetchConfig)  // eslint-disable-line @typescript-eslint/no-unused-vars

    const { setFieldValue: setLookupFieldValue, getFieldValue: getLookupFieldValue,
        getFieldReadOnly: getLookupFieldReadOnly } = useIxLookupContext()


    const contextFilters = useFiltersList()
    const setContextFilter = contextFilters.setFilter
    const { setFieldValue } = useFormikContext()

    const openLookup = useCallback((searchText: string | undefined, rangeStart?: string) => {
        if (!dataLoaded) { load(contextFilters.state).then(() => baseLookup.current?.open(searchText, rangeStart)) }
        else { baseLookup.current?.open(searchText, rangeStart) }
    }, [dataLoaded, load, contextFilters.state])


    // formating function to modify displayed value
    const format = useCallback((x: any) => {
        if (x) {
            return `${number.prefix}${x.PRINTERNAME !== null && x.PRINTERNAME !== undefined ? x.PRINTERNAME : ''}${number.suffix}`
        } else {
            return field.value ? `${number.prefix}${field.value || ''}${number.suffix}` : ''
        }
    }, [field.value, number])

    const rangeFormat = useCallback((x: any, full: boolean) => {
        if (x) {
            if (full) {
                return `${number.prefix}${x.PRINTERNAME !== null && x.PRINTERNAME !== undefined ? x.PRINTERNAME.replace('-', '/') : ''}${number.suffix}`
            } else {
                return `${x.HDR_H1_USERID ? x.HDR_H1_USERID : ''}`
            }
        } else {
            return `${field.value ? field.value : ''}`
        }
    }, [field.value, number])

    const handleSelectionChanged = useSelectionChangedCallback({
        value,
        setValue,
        selectionMode,
        displayExpr: rangeFormat
    })

    const handleInputChanged = useInputChangedCallback({
        inputRe,
        getLookupFieldReadOnly,
        loadItem,
        handleSelectionChanged,
        globalFilters: contextFilters.state,
        openLookup,
        selectionMode,
        setValue,
    })

    // call onLoadingChanged callback if available
    useEffect(() => {
        onLoadingChanged && onLoadingChanged(name, loading)
    }, [onLoadingChanged, loading, name])

    useEffect(() => {
        if (value && typeof value !== 'string' && updateField?.name && updateField?.column) {
            Array.isArray(value)
                ? setFieldValue(updateField.name, `${value.map((x: any) => x[updateField.column]).join(',')}`, true)
                : setFieldValue(updateField.name, value[updateField.column], true)
        }

    },[setFieldValue, updateField?.name, updateField?.column, value])

    // if formik value changes update lookup
    useEffect(() => {
        if (firstRender || !active || _.isEqual(field.value, lastFormikValue.current)) { return }

        if (selectionMode === 'range') {
            lastFormikValue.current = field.value
            setValue(field.value)
        } else {
            lastFormikValue.current = field.value

            if (_.isNil(value) && _.isNil(field.value)) { return }
            else if (_.isNil(field.value)) { handleSelectionChanged(null); return }

            let field_value = field.value;
            if (selectionMode === 'multicsv' && typeof(field.value) === 'string') {
                field_value = field.value.split(',').map((x: string) => ixStringToInt(x))
            }

            if (Array.isArray(field_value)) {
                if (_.isNil(value) || !_.isEqual(value.map((x: any) => x[valueExpr]), field_value)) {
                    if (field_value) {
                        load(contextFilters.state)
                            .then((data: any) => handleSelectionChanged(data.filter((x: any) => field_value.includes(x[valueExpr]))))
                            .catch(() => {})
                    } else {
                        handleSelectionChanged(field_value)
                    }
                }
            } else {
                if (_.isNil(value) || value[valueExpr] !== field_value) {
                    if (field_value) {
                        loadItem(contextFilters.state, { [valueExpr]: field_value })
                            .then((data: any) => handleSelectionChanged(data))
                            .catch(() => {})
                    } else {
                        handleSelectionChanged(field_value)
                    }
                }
            }
        }
    }, [field.value, active, firstRender, contextFilters.state, load,
        loadItem, handleSelectionChanged, selectionMode, value,
        valueExpr])

    useEffect(() => {
        if (readOnly && field.value && selectionMode !== 'range' && !_.isEqual(field.value, lastFormikValue.current)) {
            if (value !== null) { return }

            lastFormikValue.current = field.value

            if (selectionMode === 'single') {
                loadItem(contextFilters.state, { [valueExpr]: field.value })
                    .then(res => handleSelectionChanged(res))
                    .catch(() => {})
            } else {
                load(contextFilters.state)
                    .then((data: any) => handleSelectionChanged(data.filter((x: any) => field.value.includes(x[valueExpr]))))
                    .catch(() => {})
            }
        }
    }, [loadItem, load, field.value, value, contextFilters.state, readOnly,
        handleSelectionChanged, selectionMode, valueExpr])

    useEffect(() => {
        if (!firstRender) { return }

        const val = getLookupFieldValue(name)
        if (selectionMode === 'multi') {
            if (val && Array.isArray(val)) {
                const vIds = val.map((v: any) => v && typeof(v) === 'object' ? v[valueExpr] : null).filter((v: any) => v)
                if (vIds.length === val.length && _.isEqual(vIds, field.value)) {
                    setValue(val)
                    setData(val)
                    lastFormikValue.current = field.value
                }
            }
        } else if (selectionMode === 'multicsv') {
            if (val && Array.isArray(val)) {
                const vIds = val.map((v: any) => v && typeof(v) === 'object' ? v[valueExpr] : null).filter((v: any) => v).join(', ')
                if (_.isEqual(vIds, field.value)) {
                    setValue(val)
                    setData(val)
                    lastFormikValue.current = field.value
                }
            }
        } else {
            if (selectionMode === 'single' && val && typeof(val) === 'object' && field.value === val[valueExpr]) {
                setValue(val)
                setData([val])
                lastFormikValue.current = field.value
            } else if (selectionMode === 'range' && val) {
                setValue(val)
            }
        }

        if (field.value === null && val) {
            setLookupFieldValue(name, null)
        }

        setFirstRender(false)
    }, [firstRender, setContextFilter, getLookupFieldValue, field.value, name, setData,
        setLookupFieldValue, selectionMode, getFirstCtxValue, valueExpr])

    // callbacks for changes in lookup context
    const onHdrH1UseridChange = useContextSelfCallback({
        valueExpr, contextFilters: contextFilters.state, handleSelectionChanged,
        setData, loadItem, load, castToType, selectionMode
    })

    // update callbacks for fields we're interested in
    useEffect(() => {
        setCallbacks({
            [name]: onHdrH1UseridChange,
        })
    }, [name, onHdrH1UseridChange])


    const columns = useMemo(() => ([
        {
            dataField: 'HDR_ID',
            dataType: 'number',
            visible: false,
            caption: t('global:field.HDR_ID'),
        },
        {
            dataField: 'PRINTERNAME',
            dataType: 'string',
            visible: true,
            caption: t('prnsrvcprinter:field.PRINTERNAME'),
        },
        {
            dataField: 'HDR_H1_USERID',
            dataType: 'number',
            visible: true,
            caption: t('global:field.HDR_H1_USERID'),
        },
    ]), [t])



    return (
        <BaseLookup
            ref={baseLookup}
            name={name}
            label={label || t('None')}
            opts={data}
            readOnly={readOnly}
            valueExpr={valueExpr}
            keyExpr='HDR_ID'
            displayExpr={format}
            inputExpr={inputFormat}
            loading={loading}
            selectionMode={selectionMode}
            visible={visible}

            onInputChanged={handleInputChanged}
            onSelectionChanged={handleSelectionChanged}

            value={value}
            callbacks={callbacks}

            contextFilters={contextFilters}
            loadFn={load}
            resetFn={reset}

            columns={columns}

            openDropdown={openDropdown}
            onInfoChange={onInfoChange}

            shouldUpdate={shouldUpdateConfig}
            getFirstCtxValue={getFirstCtxValue}

            dataLoaded={dataLoaded}


        />
    )
}
